
// Returns true if value looks like a Unix epoch (seconds): numeric and >= 10 digits.
const isEpochSeconds = (v) =>
  (typeof v === 'number' && v > 0) ||
  (typeof v === 'string' && /^\d{10,}$/.test(v.trim()));

// Normalises any due-date value to a JS Date.
// Accepts: Unix epoch seconds (number or numeric string), ISO strings, YYYY-MM-DD.
export const parseDueDate = (value) => {
  if (!value && value !== 0) return null;
  if (isEpochSeconds(value)) {
    const d = new Date(Number(value) * 1000);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999);
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

// Formats a due-date value for display using the system's local timezone.
// Shows time when it is not midnight.
export const formatDueDate = (value) => {
  const d = parseDueDate(value);
  if (!d) return null;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const base = `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
  if (d.getHours() !== 0 || d.getMinutes() !== 0) {
    const h = d.getHours();
    const mi = d.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${base}, ${h12}:${String(mi).padStart(2,'0')} ${ampm}`;
  }
  return base;
};

// Returns true when due date is past.
export const isOverdue = (value) => {
  const due = parseDueDate(value);
  if (!due) return false;
  return due < new Date();
};

// Converts any due-date value to YYYY-MM-DDTHH:mm for datetime-local inputs.
export const toDatetimeLocal = (value) => {
  const d = parseDueDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${day}T${h}:${mi}`;
};

// Returns the current datetime in YYYY-MM-DDTHH:mm format (used as min for pickers).
export const nowDatetimeLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${day}T${h}:${mi}`;
};

// Converts a JS Date to Unix epoch seconds for API payloads.
export const toEpochSeconds = (date) => Math.floor(date.getTime() / 1000);

// Returns the number of milliseconds between two dates that fall on Saturday or Sunday.
const weekendMsBetween = (from, to) => {
  let ms = 0;
  let cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  while (cur < to) {
    const day = cur.getDay();
    const nextDay = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    if (day === 0 || day === 6) {
      const start = cur < from ? from : cur;
      const end = nextDay > to ? to : nextDay;
      if (end > start) ms += end - start;
    }
    cur = nextDay;
  }
  return ms;
};

// Computes countdown state for a due date. Returns null if more than 72 hours (3 days) away.
// Saturday and Sunday are excluded from the countdown — only weekday time is counted.
// Returns { d, h, m, s, color, overdue } where color is 'red' (<24h), 'yellow' (1d), 'green' (2-3d).
export const computeCountdown = (value) => {
  const due = parseDueDate(value);
  if (!due) return null;
  const now = new Date();
  const rawDiff = due - now;
  if (rawDiff > 72 * 3600000) return null;
  if (rawDiff <= 0) return { overdue: true, color: 'red', d: 0, h: 0, m: 0, s: 0 };
  const diff = rawDiff - weekendMsBetween(now, due);
  if (diff <= 0) return { overdue: false, color: 'green', d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const color = diff < 6 * 3600000 ? 'red' : diff < 24 * 3600000 ? 'yellow' : 'green';
  return { d, h, m, s, color, overdue: false };
};

// Returns true if a project's due date matches the given filter key.
// Filter keys: 'all' | 'overdue' | '6h' | '24h' | '3d' | '3d+'
export const matchesDueDateFilter = (project, filter) => {
  if (!filter || filter === 'all') return true;
  const due = parseDueDate(project?.due_date);
  const now = new Date();
  if (filter === 'overdue') return due ? due < now : false;
  if (!due || due < now) return false;
  const diffHours = (due - now) / 3600000;
  if (filter === '6h') return diffHours <= 6;
  if (filter === '24h') return diffHours > 6 && diffHours <= 24;
  if (filter === '3d') return diffHours > 24 && diffHours <= 72;
  if (filter === '3d+') return diffHours > 72;
  return true;
};
